import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";

import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  updateDoc,
  where
} from "firebase/firestore";
import Navbar from "../Components/Navbar/navbar";
import useEstadoSalvamento from "../Utils/useEstadoSalvamento";
import { db } from "../Config/firebase";
import { gerarSequencial } from "../Utils/sequencial";
import RelatorioCalibracaoPrensaSolosPrint from "./RelatorioCalibracaoPrensaSolosPrint";
import { aplicarTenantAtual, consultaTenantAtual } from "../Utils/tenant";

const colecaoCalibracaoSolos = "tblCalibracaoPrensasSolos";

const cargasPadrao = [0, 200, 400, 600, 800, 1000, 1200, 1400, 1600, 1800, 2000];

const estadoInicial = {
  codigo: "",
  dataCalibracao: "",
  dataVencimento: "",
  validade: "1 ano",
  empresa: "",
  obra: "",
  obraId: "",
  endereco: "",
  local: "",

  tipoEquipamento: "Máquina de ensaios por compressão",
  marca: "",
  extensometro: "",
  resolucaoExtensometro: "",
  especificacao: "",
  numeroSerie: "",
  anel: "",
  capacidade: "5000 kgf",

  unidadeLeitura: "microns",
  constanteMarshall: "",
  constanteCbr: "",
  diametroPistao: "5,00 cm",
  areaPistao: "19,63 cm2",

  padraoEquipamentoId: "",
  padraoEquipamentoNome: "",
  identificacaoPadrao: "",
  certificadoPadrao: "",

  observacao: ""
};

function criarResultadosIniciais() {
  return cargasPadrao.map((carga) => ({
    carga,
    l1: "",
    l2: "",
    media: ""
  }));
}

function normalizarDataISO(valor) {
  if (!valor) return "";
  if (typeof valor === "string" && /^\d{4}-\d{2}-\d{2}/.test(valor)) return valor.slice(0, 10);
  if (typeof valor === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(valor)) {
    const [dia, mes, ano] = valor.split("/");
    return `${ano}-${mes}-${dia}`;
  }
  if (valor?.toDate) return normalizarDataISO(valor.toDate());
  if (valor instanceof Date && !Number.isNaN(valor.getTime())) {
    const ano = valor.getFullYear();
    const mes = String(valor.getMonth() + 1).padStart(2, "0");
    const dia = String(valor.getDate()).padStart(2, "0");
    return `${ano}-${mes}-${dia}`;
  }
  return "";
}

function calcularDataVencimentoISO(dataCalibracao, validade) {
  const dataBase = normalizarDataISO(dataCalibracao);
  const texto = String(validade || "").trim().toLowerCase().replace(",", ".");
  if (!dataBase || !texto) return "";

  const quantidade = Number(texto.match(/[\d.]+/)?.[0]);
  if (!Number.isFinite(quantidade) || quantidade <= 0) return "";

  const [ano, mes, dia] = dataBase.split("-").map(Number);
  const vencimento = new Date(ano, mes - 1, dia);

  if (texto.includes("mes")) {
    vencimento.setMonth(vencimento.getMonth() + quantidade);
  } else {
    vencimento.setFullYear(vencimento.getFullYear() + quantidade);
  }

  return normalizarDataISO(vencimento);
}

function formatarDataBR(dataISO) {
  const data = normalizarDataISO(dataISO);
  if (!data) return "-";
  const [ano, mes, dia] = data.split("-");
  return `${dia}/${mes}/${ano}`;
}

function extrairNumeroSequencialCodigo(valor) {
  const texto = String(valor || "");
  const match = texto.match(/RE-CAL-SOLOS\s*-\s*(\d+)\s*\/\s*\d{4}/i);
  const numero = Number(match?.[1] || texto.match(/\d+/)?.[0] || 0);
  return Number.isFinite(numero) ? numero : 0;
}

function formatarCodigo(numero, ano) {
  return `RE-CAL-SOLOS - ${String(Number(numero) || 0).padStart(4, "0")}/${ano}`;
}

function obterNumeroCertificadoPadrao(equipamento) {
  const certificadoAtivo = equipamento?.certificadoAtivo || {};
  return (
    equipamento?.numeroCertificadoPadrao ||
    equipamento?.numeroCertificado ||
    certificadoAtivo?.numeroCertificado ||
    certificadoAtivo?.numero ||
    certificadoAtivo?.certificado ||
    ""
  );
}

function numeroDeTexto(valor) {
  const texto = String(valor || "")
    .replace(/[^\d,.-]/g, "")
    .replace(/\.(?=\d{3}(?:\D|$))/g, "")
    .replace(",", ".");
  const numero = Number(texto);
  return Number.isFinite(numero) ? numero : null;
}

function formatarNumeroBR(valor, casas) {
  if (!Number.isFinite(valor)) return "";
  return valor.toLocaleString("pt-BR", {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas
  });
}

function calcularMediaDuasLeituras(item) {
  const l1 = numeroDeTexto(item?.l1);
  const l2 = numeroDeTexto(item?.l2);

  return Number.isFinite(l1) && Number.isFinite(l2)
    ? formatarNumeroBR((l1 + l2) / 2, 1)
    : "";
}

function normalizarResultadosDuasLeituras(lista) {
  return (Array.isArray(lista) ? lista : criarResultadosIniciais()).map((item) => {
    return {
      carga: item.carga ?? "",
      l1: item.l1 ?? "",
      l2: item.l2 ?? "",
      media: calcularMediaDuasLeituras(item)
    };
  });
}

function calcularConstantes(resultados, areaPistao) {
  const fatoresMarshall = resultados.reduce((lista, item) => {
    const carga = numeroDeTexto(item.carga);
    const media = numeroDeTexto(calcularMediaDuasLeituras(item) || item.media);

    if (!Number.isFinite(carga) || !Number.isFinite(media) || media <= 0) {
      return lista;
    }

    return [...lista, carga / media];
  }, []);

  const mediaInicial =
    fatoresMarshall.length > 0
      ? fatoresMarshall.reduce((total, fator) => total + fator, 0) / fatoresMarshall.length
      : null;

  const fatoresFiltrados = Number.isFinite(mediaInicial) && mediaInicial !== 0
    ? fatoresMarshall.filter((fator) => {
        const diferencaPercentual = Math.abs((fator - mediaInicial) / mediaInicial) * 100;
        return diferencaPercentual <= 2;
      })
    : [];

  const constanteMarshall =
    fatoresFiltrados.length > 0
      ? fatoresFiltrados.reduce((total, fator) => total + fator, 0) / fatoresFiltrados.length
      : null;
  const area = numeroDeTexto(areaPistao);
  const constanteCbr =
    Number.isFinite(constanteMarshall) && Number.isFinite(area) && area > 0
      ? constanteMarshall / area
      : null;

  return {
    constanteMarshall: Number.isFinite(constanteMarshall)
      ? formatarNumeroBR(constanteMarshall, 4)
      : "",
    constanteCbr: Number.isFinite(constanteCbr) ? formatarNumeroBR(constanteCbr, 4) : ""
  };
}

export default function CalibracaoPrensaSolos() {
  const { salvandoCadastro, executarSalvamento } = useEstadoSalvamento();
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();
  const clonarId = id === "novo" ? new URLSearchParams(location.search).get("clonar") : null;
  const [form, setForm] = useState(estadoInicial);
  const [resultados, setResultados] = useState(criarResultadosIniciais());
  const [obras, setObras] = useState([]);
  const [obraSelecionada, setObraSelecionada] = useState(null);
  const [pesquisaObra, setPesquisaObra] = useState("");
  const [equipamentosPadrao, setEquipamentosPadrao] = useState([]);
  const [equipamentoPadraoSelecionado, setEquipamentoPadraoSelecionado] = useState(null);
  const [pesquisaEquipamento, setPesquisaEquipamento] = useState("");
  const [modoPreview, setModoPreview] = useState(false);

  useEffect(() => {
    carregarObras();
    carregarEquipamentosPadrao();
    carregarCalibracao();
  }, [id, clonarId]);

  async function carregarObras() {
    const q = query(collection(db, "obras"), where("ativo", "==", true), orderBy("nome"));
    const snap = await getDocs(consultaTenantAtual(q));
    setObras(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
  }

  async function carregarEquipamentosPadrao() {
    const [equipSnap, docsSnap] = await Promise.all([
      getDocs(consultaTenantAtual(query(collection(db, "equipamentos"), orderBy("nome")))),
      getDocs(consultaTenantAtual(collection(db, "eq_documentos")))
    ]);

    const certificadosPorEquipamento = {};
    docsSnap.docs.forEach((item) => {
      const documento = item.data();
      if (!documento.equipamentoId) return;
      certificadosPorEquipamento[documento.equipamentoId] = documento;
    });

    setEquipamentosPadrao(
      equipSnap.docs
        .map((item) => ({
          id: item.id,
          ...item.data(),
          certificadoAtivo: certificadosPorEquipamento[item.id] || null
        }))
        .filter((equipamento) => equipamento.ativo !== false)
    );
  }

  async function carregarCalibracao() {
    if ((!id || id === "novo") && !clonarId) return;

    const snap = await getDoc(doc(db, colecaoCalibracaoSolos, clonarId || id));
    if (!snap.exists()) return;

    const dados = snap.data();
    const dadosFormulario = {
  ...estadoInicial,
  ...dados,

  tipoEquipamento:
    dados.tipoEquipamento === "Prensa CBR / Marshall" ||
    dados.tipoEquipamento === "Prensa CBR" ||
    dados.tipoEquipamento === "Prensa Marshall"
      ? "Máquina de ensaios por compressão"
      : dados.tipoEquipamento ||
        "Máquina de ensaios por compressão",

  extensometro:
    dados.extensometro ||
    dados.modelo ||
    "",

  resolucaoExtensometro:
    dados.resolucaoExtensometro ||
    dados.resolucao ||
    "",

  numeroSerie:
    dados.numeroSerie ||
    dados.serie ||
    "",

  anel:
    dados.anel ||
    dados.patrimonio ||
    "",

  unidadeLeitura:
    String(dados.unidadeLeitura || "").toLowerCase() === "kgf"
      ? "microns"
      : dados.unidadeLeitura ||
        estadoInicial.unidadeLeitura,

  dataVencimento:
    dados.dataVencimento ||
    calcularDataVencimentoISO(
      dados.dataCalibracao,
      dados.validade
    )
};

    setForm(clonarId ? {
      ...Object.fromEntries(Object.keys(estadoInicial).map((campo) => [campo, dadosFormulario[campo] ?? estadoInicial[campo]])),
      codigo: "",
      dataCalibracao: "",
      dataVencimento: "",
      constanteMarshall: dadosFormulario.constanteMarshall || "",
      constanteCbr: dadosFormulario.constanteCbr || "",
    } : dadosFormulario);

    setResultados(normalizarResultadosDuasLeituras(dados.resultados));

    if (dados.obraId || dados.obra) {
      setObraSelecionada({ id: dados.obraId, nome: dados.obra });
    }
    if (dados.padraoEquipamentoId) {
      setEquipamentoPadraoSelecionado({
        id: dados.padraoEquipamentoId,
        nome: dados.padraoEquipamentoNome || dados.identificacaoPadrao || ""
      });
    }
  }

  async function obterProximoCodigo() {
    const anoAtual = new Date().getFullYear();
    const snap = await getDocs(consultaTenantAtual(collection(db, colecaoCalibracaoSolos)));
    const maior = snap.docs.reduce((atual, item) => {
      const dados = item.data();
      const data = String(dados.dataCalibracao || "");
      const anoRegistro = /^\d{4}/.test(data) ? Number(data.slice(0, 4)) : anoAtual;
      if (anoRegistro !== anoAtual) return atual;
      return Math.max(atual, extrairNumeroSequencialCodigo(dados.codigo));
    }, 0);

    return formatarCodigo(maior + 1, anoAtual);
  }

  function aplicarEquipamentoPadrao(equipamento) {
    setEquipamentoPadraoSelecionado(equipamento);
    setForm((anterior) => ({
      ...anterior,
      padraoEquipamentoId: equipamento?.id || "",
      padraoEquipamentoNome: equipamento?.nome || "",
      identificacaoPadrao: equipamento?.identificacaoPadrao || equipamento?.descricao || equipamento?.nome || "",
      certificadoPadrao: equipamento ? obterNumeroCertificadoPadrao(equipamento) : ""
    }));
  }

  function atualizarResultado(index, campo, valor) {
    const lista = [...resultados];
    lista[index] = { ...lista[index], [campo]: valor };
    lista[index].media = calcularMediaDuasLeituras(lista[index]);

    setResultados(lista);
  }

  function handleKeyDown(event, rowIndex, field) {
    if (event.key !== "Enter") return;
    event.preventDefault();
    document.querySelector(`[data-row="${rowIndex + 1}"][data-field="${field}"]`)?.focus();
  }

  async function salvar() {
    try {
      const dataVencimento = calcularDataVencimentoISO(form.dataCalibracao, form.validade);
      const resultadosNormalizados = normalizarResultadosDuasLeituras(resultados);
      const constantes = calcularConstantes(resultadosNormalizados, form.areaPistao);
      let dadosParaSalvar = {
        ...form,
        ...constantes,
        dataVencimento,
        resultados: resultadosNormalizados
      };

      if (id && id !== "novo") {
        await updateDoc(doc(db, colecaoCalibracaoSolos, id), dadosParaSalvar);
      } else {
        if (!/^RE-CAL-SOLOS\s*-\s*\d+\/\d{4}$/i.test(String(form.codigo || "").trim())) {
          const ano = new Date(form.dataCalibracao || new Date()).getFullYear();
          const codigoLegado = await obterProximoCodigo();
          const sequencialLegado = Number(String(codigoLegado || "").match(/(\d+)/)?.[1] || 1);
          const proximoCodigo = await gerarSequencial({
            chave: "calibracao_prensa_solos",
            escopo: ano,
            formato: {
              padding: 4,
              ano,
              template: "RE-CAL-SOLOS - {numero}/{ano}",
            },
            valorMinimo: sequencialLegado - 1,
          });
          dadosParaSalvar = {
            ...dadosParaSalvar,
            codigo: proximoCodigo.codigo
          };
        }

        await addDoc(collection(db, colecaoCalibracaoSolos), aplicarTenantAtual({
          ...dadosParaSalvar,
          criado: new Date()
        }));
      }

      history.goBack();
    } catch (erro) {
      console.log(erro);
      alert("Erro ao salvar calibração de prensa de solos");
    }
  }

  const dataVencimentoExibicao =
    form.dataVencimento || calcularDataVencimentoISO(form.dataCalibracao, form.validade);
  const constantesCalculadas = calcularConstantes(resultados, form.areaPistao);
  const dadosRelatorio = {
    ...form,
    ...constantesCalculadas,
    dataVencimento: dataVencimentoExibicao,
    resultados: normalizarResultadosDuasLeituras(resultados)
  };

  if (modoPreview) {
    return (
      <RelatorioCalibracaoPrensaSolosPrint
        dados={dadosRelatorio}
        onVoltar={() => setModoPreview(false)}
      />
    );
  }

  return (
    <>
      <Navbar />
      <Box sx={{ px: 3, pb: 3, pt: "88px" }}>
        <Box
          mb={3}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={3}
          sx={{
            position: "fixed",
            top: 60,
            left: 0,
            right: 0,
            zIndex: 1190,
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
            px: 3,
            py: 1.5,
            boxShadow: 2
          }}
        >
          <Typography variant="h5">Cadastro de Calibração de Prensa - Solos</Typography>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" onClick={() => history.goBack()}>Voltar</Button>
            <Button variant="outlined" onClick={() => setModoPreview(true)}>Visualizar</Button>
            <Button variant="contained" onClick={() => executarSalvamento(salvar)} disabled={salvandoCadastro}>
              {salvandoCadastro ? "Salvando..." : "Salvar"}
            </Button>
          </Stack>
        </Box>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack spacing={2}>
           <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
  <TextField
    label="Código"
    value={form.codigo}
    onChange={(e) =>
      setForm({
        ...form,
        codigo: e.target.value
      })
    }
    sx={{ width: { md: 190 } }}
  />

  <TextField
    type="date"
    label="Data de calibração"
    value={form.dataCalibracao}
    onChange={(e) =>
      setForm({
        ...form,
        dataCalibracao: e.target.value
      })
    }
    InputLabelProps={{ shrink: true }}
    sx={{ width: { md: 180 } }}
  />

  <TextField
    label="Validade"
    value={form.validade}
    onChange={(e) =>
      setForm({
        ...form,
        validade: e.target.value
      })
    }
    sx={{ width: { md: 140 } }}
  />

  <TextField
    label="Vencimento"
    value={formatarDataBR(dataVencimentoExibicao)}
    InputProps={{ readOnly: true }}
    sx={{ width: { md: 160 } }}
  />
</Stack>

            <Stack spacing={2}>
              <Autocomplete
                fullWidth
                options={obras}
                getOptionLabel={(option) => option?.nome || ""}
                value={obraSelecionada}
                inputValue={pesquisaObra}
                onInputChange={(event, newValue) => setPesquisaObra(newValue)}
                onChange={(event, newValue) => {
                  setObraSelecionada(newValue);
                  setForm({
                    ...form,
                    obra: newValue?.nome || "",
                    obraId: newValue?.id || "",
                    empresa: newValue?.clienteNome || "",
                    endereco: newValue?.endereco || "",
                    local: newValue?.cidade || newValue?.endereco || ""
                  });
                }}
                renderInput={(params) => <TextField {...params} label="Empreendimento" placeholder="Pesquisar obra..." />}
              />
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField label="Cliente" value={form.empresa} InputProps={{ readOnly: true }} sx={{ flex: 1 }} />
                <TextField label="Local" value={form.local} onChange={(e) => setForm({ ...form, local: e.target.value })} sx={{ flex: 1 }} />
              </Stack>
            </Stack>

              <Typography variant="subtitle1" fontWeight={700}>
                Padrão utilizado na calibração
              </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Autocomplete
                sx={{ flex: 1.4 }}
                options={equipamentosPadrao}
                getOptionLabel={(option) => option?.nome || ""}
                value={equipamentoPadraoSelecionado}
                inputValue={pesquisaEquipamento}
                onInputChange={(event, newValue) => setPesquisaEquipamento(newValue)}
                onChange={(event, newValue) => aplicarEquipamentoPadrao(newValue)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="Padrão utilizado" placeholder="Selecionar equipamento padrão" />}
              />
              <TextField label="Identificação padrão" value={form.identificacaoPadrao} onChange={(e) => setForm({ ...form, identificacaoPadrao: e.target.value })} sx={{ flex: 1 }} />
              <TextField label="Certificado padrão" value={form.certificadoPadrao} onChange={(e) => setForm({ ...form, certificadoPadrao: e.target.value })} sx={{ width: { md: 180 } }} />
            </Stack>

         <Typography variant="subtitle1" fontWeight={700}>
  Equipamento calibrado
</Typography>

<Stack spacing={2}>
  <Stack
    direction={{ xs: "column", md: "row" }}
    spacing={2}
  >
    <TextField
      label="Tipo do equipamento"
      value={form.tipoEquipamento}
      onChange={(e) =>
        setForm({
          ...form,
          tipoEquipamento: e.target.value
        })
      }
      sx={{ flex: 1.6 }}
    />

    <TextField
      label="Marca"
      value={form.marca}
      onChange={(e) =>
        setForm({
          ...form,
          marca: e.target.value
        })
      }
      sx={{ flex: 1 }}
    />

    <TextField
      label="Extensômetro"
      value={form.extensometro}
      onChange={(e) =>
        setForm({
          ...form,
          extensometro: e.target.value
        })
      }
      placeholder="Ex.: Relógio Mitutoyo"
      sx={{ flex: 1.4 }}
    />

    <TextField
      label="Resolução  extensômetro"
      value={form.resolucaoExtensometro}
      onChange={(e) =>
        setForm({
          ...form,
          resolucaoExtensometro: e.target.value
        })
      }
      placeholder="Ex.: 0,001 mm"
      sx={{ flex: 1.2 }}
    />
  </Stack>

  <Stack
    direction={{ xs: "column", md: "row" }}
    spacing={2}
  >
    <TextField
      label="Especificação"
      value={form.especificacao}
      onChange={(e) =>
        setForm({
          ...form,
          especificacao: e.target.value
        })
      }
      placeholder="Ex.: Prensa CBR - Manual"
      sx={{ flex: 1.5 }}
    />

    <TextField
      label="Número de série"
      value={form.numeroSerie}
      onChange={(e) =>
        setForm({
          ...form,
          numeroSerie: e.target.value
        })
      }
      sx={{ flex: 1 }}
    />

    <TextField
      label="Anel"
      value={form.anel}
      onChange={(e) =>
        setForm({
          ...form,
          anel: e.target.value
        })
      }
      sx={{ flex: 0.8 }}
    />

    <TextField
      label="Capacidade"
      value={form.capacidade}
      onChange={(e) =>
        setForm({
          ...form,
          capacidade: e.target.value
        })
      }
      placeholder="Ex.: 5000 kgf"
      sx={{ flex: 1 }}
    />
  </Stack>
</Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField label="Constante Marshall" value={constantesCalculadas.constanteMarshall} InputProps={{ readOnly: true }} />
              <TextField label="Constante C.B.R." value={constantesCalculadas.constanteCbr} InputProps={{ readOnly: true }} />
              <TextField label="Diâmetro do pistão" value={form.diametroPistao} onChange={(e) => setForm({ ...form, diametroPistao: e.target.value })} />
              <TextField label="Área" value={form.areaPistao} onChange={(e) => setForm({ ...form, areaPistao: e.target.value })} />
              <TextField label="Unidade das leituras" value={form.unidadeLeitura} onChange={(e) => setForm({ ...form, unidadeLeitura: e.target.value })} />
            </Stack>

            <TextField label="Observação" multiline minRows={2} value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} />
          </Stack>
        </Paper>

        <Paper sx={{ p: 2, maxWidth: 920, mx: "auto", width: "100%" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ fontWeight: 700 }}>
                  Tabela de resultados - valores em kgf
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell rowSpan={2} align="center" sx={{ width: 170 }}>
                  Carga aplicada (padrão)
                </TableCell>
                <TableCell colSpan={3} align="center">
                  DEFORMAÇÃO DO ANEL DINAMOMÉTRICO
                  <br />
                  (valores em {form.unidadeLeitura || "microns"})
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="center">Leitura 1</TableCell>
                <TableCell align="center">Leitura 2</TableCell>
                <TableCell align="center">Média</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resultados.map((item, index) => (
                <TableRow key={`${item.carga}-${index}`}>
                  <TableCell>
                    <TextField size="small" value={item.carga} onChange={(e) => atualizarResultado(index, "carga", e.target.value)} inputProps={{ style: { textAlign: "center" } }} sx={{ maxWidth: 130 }} />
                  </TableCell>
                  {["l1", "l2"].map((campo) => (
                    <TableCell key={campo}>
                      <TextField
                        size="small"
                        value={item[campo]}
                        onChange={(e) => atualizarResultado(index, campo, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index, campo)}
                        inputProps={{
                          "data-row": index,
                          "data-field": campo,
                          style: { textAlign: "center" }
                        }}
                        sx={{ maxWidth: 220 }}
                      />
                    </TableCell>
                  ))}
                  <TableCell align="center">{calcularMediaDuasLeituras(item) || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>

    </>
  );
}
